import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAPI } from '../../../Contexts/APIContext'
import useAllCollections from '../../../Helpers/hooks/useAllCollections'
import { Breadcrumbs } from '../../../Components/GeneralComponents/Breadcrumbs/Breadcrumbs'
import { ButtonOutline } from '../../../Components/GeneralComponents/ButtonOutline/ButtonOutline'
import { formatStringInNav } from '../../../Helpers/String+Extension'
import Loader from '../../../Components/GeneralComponents/Loader'
import PublicationBadge from '../../../Components/GeneralComponents/PublicationBadge'
import RefreshBadge from '../../../Components/GeneralComponents/RefreshBadge'
// import TagBubble from '../../Components/DashboardComponents/TagInputSection/TagBubble'
import TagInputSection from '../../../Components/DashboardComponents/TagInputSection'
import Prompt from '../../../Components/DashboardComponents/Prompt/Prompt'
import classes from './index.module.css'
import { StickyHeader } from '../../../Components/StickyHeader'
import {
  convertMillisecondsToSeconds,
  isPlural,
} from '../../../services/functions'
import { permissions } from '../../../constants/roles'
import useReadOnly from '../../../services/hooks/useReadOnly'
import useAdminAccess from '../../../services/hooks/useAdminAccess'
import { useAuth } from '../../../Contexts/AuthContext'
import { useCurrentItem } from '../../../Contexts/CurrentItemContext'
import useGetArtistData from '../../../Helpers/hooks/useGetArtistData'
import { useGenerateTags } from '../CreateFeel/Step2/useGenerateTags'
import errorToast from '../../../Components/Toasts/ErrorToast'
import successToast from '../../../Components/Toasts/SuccessToast'
import { usePreventFullscreen } from '../../../Helpers/hooks/usePreventFullscreen'

export default function Feel() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const {
    getFeelData,
    publishFeel,
    unpublishFeel,
    deleteFeel,
    updateTagsOnFeel,
    refreshFeel,
    getFeelTags,
    getArtistPinnedFeels,
    updateArtistPinnedMM,
    getSongData,
    addFeelsToCollection,
    getCollection,
    submitReport,
  } = useAPI()
  const { getCurrentUser } = useAuth()
  const { id } = useParams()
  const videoElement = useRef()
  usePreventFullscreen(videoElement)

  const { isInternal } = useAdminAccess()

  const [isLoadinAction, setIsLoadingAction] = useState(false)
  const [isPromptDelete, setIsPromptDelete] = useState(false)
  const [suggestedTags, setSuggestedTags] = useState(null)
  const [showPinTooltip, setShowPinTooltip] = useState(false)
  const [isPinned, setIsPinned] = useState(false)
  const [isPinActionLoading, setIsPinActionLoading] = useState(false)
  const [isPromptPin, setIsPromptPin] = useState(false)
  const [showCollectionModal, setShowCollectionModal] = useState(false)
  const [collectionSearch, setCollectionSearch] = useState('')
  const [addedCollectionIds, setAddedCollectionIds] = useState(new Set())
  const [syncReported, setSyncReported] = useState(false)
  const [refreshSubmitted, setRefreshSubmitted] = useState(false)

  // data feel
  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: ['FeelData', id],
    queryFn: () => getFeelData(id),
  })

  const handleCopyShareURL = () => {
    const shareBaseURL = process.env.REACT_APP_IS_DEV === 'true'
      ? 'https://feelsmusic.dev'
      : 'https://feelsmusic.app'
    navigator.clipboard.writeText(`${shareBaseURL}/mm/${data.f_id}`)
      .then(() => successToast('Share link copied to clipboard'))
      .catch(() => errorToast('Failed to copy share link'))
  }

  // Check permissions - org-aware based on feel's organizations
  const { isReadOnly: isFeelsReadOnly } = useReadOnly(permissions.r.feels_mm, data)

  // Fetch artist data for navbar permission badge (artist always has organizations)
  const { data: artistData } = useGetArtistData({
    artistName: data?.artist?.artist_name,
    enabled: !!data?.artist?.artist_name,
  })
  const { setItem } = useCurrentItem()
  useEffect(() => {
    if (artistData) {
      setItem(artistData)
    }
  }, [artistData, setItem])

  // Fetch song data to get publication status
  const { data: songData } = useQuery({
    queryKey: ['SongData', data?.song_f_id],
    queryFn: () => getSongData(data?.song_f_id),
    enabled: !!data?.song_f_id,
  })


  useEffect(() => {
    const checkPinnedStatus = async () => {
      if (data?.is_promotional && data?.artist?.artist_name) {
        try {
          const pinnedResponse = await getArtistPinnedFeels(data.artist.artist_name)
          
          // Check if current feel's f_id is in the pinned feels array
          if (pinnedResponse?.feels && Array.isArray(pinnedResponse.feels)) {
            const isPinnedFeel = pinnedResponse.feels.some(feel => feel.f_id === id)
            setIsPinned(isPinnedFeel)
          }
        } catch (error) {
          // Error checking pinned status
        }
      }
    }
    
    checkPinnedStatus()
  }, [data?.is_promotional, data?.artist?.artist_name, id, getArtistPinnedFeels])

  const { data: officialTags, isLoading: isFetchingOfficialTags } = useQuery({
    queryKey: ['officialTags', id],
    queryFn: () => getFeelTags({ feelFID: id }),
    enabled: !!data && !!id,
  })

  const { data: userTags, isLoading: isFetchingUserTags } = useQuery({
    queryKey: ['userTags', id],
    queryFn: () => getFeelTags({ feelFID: id, isUserTags: true }),
    enabled: !!data && !!id,
  })


  const { data: suggestedTagsList, isLoading: isFetchingSuggestedTags } =
    useQuery({
      queryKey: ['suggestedTags', id],
      queryFn: () => getFeelTags({ feelFID: id, isSuggestedTags: true }),
      enabled: !!data && !!id,
    })

  // Fetch all collections for the add-to-collection modal
  const { data: apiCollections, isLoading: isLoadingCollections, isError: isCollectionsError } = useAllCollections({ enabled: isInternal })

  const sortedCollections = useMemo(() => {
    if (!apiCollections) return []
    return [...apiCollections].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  }, [apiCollections])

  const filteredCollections = useMemo(() => {
    if (!collectionSearch.trim()) return sortedCollections
    const term = collectionSearch.toLowerCase()
    return sortedCollections.filter(c => (c.name || '').toLowerCase().includes(term))
  }, [sortedCollections, collectionSearch])

  const closeCollectionModal = useCallback(() => {
    setShowCollectionModal(false)
    setCollectionSearch('')
  }, [])

  // Escape key to close collection modal
  useEffect(() => {
    if (!showCollectionModal) return
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeCollectionModal()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [showCollectionModal, closeCollectionModal])

  const addToCollectionMutation = useMutation({
    mutationFn: async ({ collectionFID, feelFID }) => {
      // Check if the feel is already in this collection before adding
      const existingFIDs = []
      let page = 1
      const pageLimit = 20
      while (true) {
        const res = await getCollection(collectionFID, page, pageLimit)
        const feels = res.feels || []
        existingFIDs.push(...feels.map(f => f.f_id))
        if (existingFIDs.includes(feelFID)) {
          throw new Error('ALREADY_EXISTS')
        }
        if (feels.length < pageLimit) break
        page++
      }
      return addFeelsToCollection(collectionFID, [feelFID])
    },
    onSuccess: (_, { collectionFID }) => {
      successToast('Feel added to collection')
      setAddedCollectionIds(prev => new Set(prev).add(collectionFID))
    },
    onError: (error, { collectionFID }) => {
      if (error.message === 'ALREADY_EXISTS') {
        errorToast('Feel is already in this collection')
        setAddedCollectionIds(prev => new Set(prev).add(collectionFID))
      } else {
        errorToast('Failed to add feel to collection')
      }
    },
  })

  // Report Sync mutation
  const reportSyncMutation = useMutation({
    mutationFn: async (feelFID) => {
      const currentUser = getCurrentUser()
      return submitReport({
        report_type: 'NEEDS_SYNC',
        report_text: '',
        object: 'feels',
        object_f_id: feelFID,
        report_origin: 'FAST',
        user_f_id: currentUser?.userId || '',
        report_user_id: currentUser?.email || '',
        department: 'Feels Factory',
        report_data: {
          source: 'feel_detail',
        },
      })
    },
    onSuccess: () => {
      successToast('Needs Sync report submitted')
      setSyncReported(true)
    },
    onError: (e) => {
      errorToast(`Error submitting report: ${e.message ?? ''}`)
    },
  })

  // unpublish feel
  const unpublishMutation = useMutation({
    mutationFn: () => unpublishFeel(id),
    onSuccess: data => {
      queryClient.clear()
      setIsLoadingAction(false)
    },
    onError: () => {
      setIsLoadingAction(false)
    },
  })

  const handleOnUnpublish = () => {
    setIsLoadingAction(true)
    unpublishMutation.mutate()
  }

  // publish feel
  const publishMutation = useMutation({
    mutationFn: () => publishFeel(id),
    onSuccess: data => {
      queryClient.clear()
      setIsLoadingAction(false)
    },
    onError: err => {
      if (err.status === 400 && err.type === 'cors') {
        errorToast(
          'This item isn’t ready to publish yet. Please give it a moment and try again.'
        )
      } else {
        const message =
          typeof err?.message === 'string' && err.message
            ? err.message
            : 'Something went wrong. Please try again later.'
        errorToast(message)
      }
      setIsLoadingAction(false)
    },
    meta: {
      suppressGlobalError: true,
    },
  })

  const handleOnPublish = () => {
    setIsLoadingAction(true)
    publishMutation.mutate()
  }

  // delete feel
  const deleteMutation = useMutation({
    mutationFn: () => deleteFeel(id),
    onSuccess: data => {
      queryClient.clear()
      navigate(
        data?.is_promotional
          ? '/tease/feels/' + data?.song_f_id
          : '/song/feels/' + data?.song_f_id
      )
      setIsLoadingAction(false)
    },
    onError: () => {
      setIsPromptDelete(false)
      setIsLoadingAction(false)
    },
  })

  const handleOnDelete = () => {
    setIsLoadingAction(true)
    deleteMutation.mutate()
  }

  // update tags
  const updateTagsMutation = useMutation({
    mutationFn: newTags => updateTagsOnFeel(id, newTags),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['FeelData', id],
      })
      queryClient.invalidateQueries({
        queryKey: ['officialTags', id],
      })
      queryClient.invalidateQueries({
        queryKey: ['userTags', id],
      })
    },
  })

  const handleUpdateTags = newTags => {
    updateTagsMutation.mutate(newTags)
  }

  // refresh feel — re-renders feel against current song state
  const refreshMutation = useMutation({
    mutationFn: () => refreshFeel(data?.f_id, data?.song_f_id, data?.lyrics_lines),
    onSuccess: () => {
      setRefreshSubmitted(true)
      queryClient.invalidateQueries({ queryKey: ['FeelData', id] })
      if (data?.song_f_id) {
        queryClient.invalidateQueries({ queryKey: ['songFeelsList', data.song_f_id] })
      }
    },
    onError: err => {
      const message =
        typeof err?.message === 'string' && err.message
          ? err.message
          : 'Failed to queue refresh. Try again.'
      errorToast(message)
    },
    meta: { suppressGlobalError: true },
  })

  function handleOnTimeUpdate() {
    if (videoElement.current.currentTime >= videoElement.current.duration) {
      videoElement.current.currentTime = 0
      videoElement.current.play()
    }
  }

  const {
    mutate: generateSuggestedTags,
    isPending: isLoadingGenerateSuggestedTags,
    isError: isErrorGenerateSuggestedTags,
  } = useGenerateTags()

  const handleGenerateTags = data => {
    generateSuggestedTags(data, {
      onSuccess: data => {
        setSuggestedTags(data)
        successToast('Suggested tags generated successfully')
      },
      onError: error => {
        errorToast('Error generating Suggested tags', error)
      },
    })
  }

  const handlePinToggle = () => {
    if (isPinned) {
      // Unpin directly without confirmation
      performPinToggle()
    } else {
      // Show confirmation dialog for pinning
      setIsPromptPin(true)
    }
  }

  const performPinToggle = async () => {
    if (!data?.artist?.artist_name) return
    
    setIsPromptPin(false)
    setIsPinActionLoading(true)
    try {
      const feelsFIDs = isPinned ? [] : [id]
      
      const response = await updateArtistPinnedMM(data.artist.artist_name, feelsFIDs)
      
      // Toggle the pinned state
      setIsPinned(!isPinned)
      successToast(isPinned ? 'Feels unpinned successfully' : 'Feels pinned successfully')
    } catch (error) {
      errorToast(`Error ${isPinned ? 'unpinning' : 'pinning'} feel`)
    } finally {
      setIsPinActionLoading(false)
    }
  }

  useEffect(() => {
    suggestedTagsList && setSuggestedTags(suggestedTagsList)
  }, [suggestedTagsList])

  return (
    <div className={classes.wrapper}>
      {isPromptDelete && (
        <Prompt
          title={'Delete Feels'}
          subtitle={
            'Are you sure you want to delete this Feels? This action is permanent.'
          }
          primaryAction={'CANCEL'}
          onPrimaryClick={() => {
            setIsPromptDelete(false)
          }}
          secondaryAction={'DELETE'}
          onSecondaryClick={() => {
            handleOnDelete()
          }}
          isSecondaryActionDestructive={true}
          onCloseClick={() => {
            setIsPromptDelete(false)
          }}
        />
      )}
      {isPromptPin && (
        <div
          className="prompt-bg-container"
          onClick={() => setIsPromptPin(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(64, 64, 64, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div 
            className="prompt-container"
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: 'black',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '480px',
              width: '90%',
            }}
          >
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{ 
                fontSize: '20px', 
                fontWeight: '600',
                marginBottom: '12px',
                color: 'white'
              }}>
                Pin this Tease?
              </h2>
              <p style={{ 
                fontSize: '14px',
                color: '#ccc',
                lineHeight: '1.5'
              }}>
                This Tease will be pinned to the first slot in the Feels section of your Artist Profile, replacing any previously pinned Tease.
              </p>
            </div>
            <div style={{ 
              display: 'flex', 
              gap: '12px',
              justifyContent: 'center' 
            }}>
              <ButtonOutline
                color={'green'}
                size="small"
                text={'PIN TEASE'}
                onClick={() => performPinToggle()}
              />
              <ButtonOutline
                color={'pink'}
                size="small"
                text={'CANCEL'}
                onClick={() => setIsPromptPin(false)}
              />
            </div>
          </div>
        </div>
      )}
      {showCollectionModal && (
        <div
          className={classes.collectionModalOverlay}
          onClick={closeCollectionModal}
        >
          <div
            className={classes.collectionModalContent}
            onClick={e => e.stopPropagation()}
          >
            <div className={classes.collectionModalHeader}>
              <h3 className={classes.collectionModalTitle}>Add to Collection</h3>
              <button
                className={classes.collectionModalClose}
                onClick={closeCollectionModal}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <input
              type="text"
              className={classes.collectionSearchInput}
              placeholder="Search collections..."
              value={collectionSearch}
              onChange={e => setCollectionSearch(e.target.value)}
              autoFocus
            />
            <div className={classes.collectionList}>
              {isLoadingCollections ? (
                <div className={classes.collectionNoResults}>Loading collections...</div>
              ) : isCollectionsError ? (
                <div className={classes.collectionNoResults}>Failed to load collections</div>
              ) : filteredCollections.length === 0 ? (
                <div className={classes.collectionNoResults}>
                  {collectionSearch.trim() ? 'No matching collections' : 'No collections found'}
                </div>
              ) : (
                filteredCollections.map(c => {
                  const isAdded = addedCollectionIds.has(c.f_id)
                  const isAdding = addToCollectionMutation.isPending && addToCollectionMutation.variables?.collectionFID === c.f_id
                  return (
                    <div key={c.f_id} className={classes.collectionListItem}>
                      <span className={(c.list?.is_public ?? c.is_public) ? classes.collectionDotPublic : classes.collectionDotPrivate} />
                      <span className={classes.collectionListName}>{c.name}</span>
                      {isAdded ? (
                        <span className={classes.collectionAdded}>Added</span>
                      ) : (
                        <ButtonOutline
                          color="green"
                          size="small"
                          text={isAdding ? '...' : 'ADD'}
                          disable={addToCollectionMutation.isPending}
                          onClick={() => addToCollectionMutation.mutate({ collectionFID: c.f_id, feelFID: id })}
                        />
                      )}
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </div>
      )}
      {!isLoading && data && (
        <StickyHeader>
          <Breadcrumbs
            items={
              !data?.artist?.name
                ? [
                    {
                      text: data.is_promotional ? 'All Artists' : 'All Songs',
                      onClick: () => {
                        navigate(
                          data.is_promotional ? '/artists' : '/songs/all'
                        )
                      },
                    },
                  ]
                : [
                    {
                      text: data.is_promotional ? 'All Artists' : 'All Songs',
                      onClick: () => {
                        navigate(
                          data.is_promotional ? '/artists' : '/songs/all'
                        )
                      },
                    },
                    {
                      text: formatStringInNav(data?.artist?.name),
                      onClick: () => {
                        navigate('/artist/' + data.artist.artist_name)
                      },
                    },
                    {
                      text: data.is_promotional ? 'Teases' : 'Songs',
                      onClick: () => {
                        navigate(
                          data.is_promotional
                            ? '/teases/artist/' + data.artist.artist_name
                            : '/songs/artist/' + data.artist.artist_name
                        )
                      },
                    },
                    {
                      text: formatStringInNav(data?.song_name),
                      onClick: () => {
                        navigate(
                          data.is_promotional
                            ? '/tease/' + data.song_f_id
                            : '/song/' + data.song_f_id
                        )
                      },
                    },
                    {
                      text: 'Feels',
                      onClick: () => {},
                    },
                  ]
            }
          />
          <div className={classes.titleContainer}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <h1 className={classes.title}>View Feels</h1>
              {isPinned && (
                <div
                  style={{
                    padding: '5px 10px',
                    borderRadius: '15px',
                    backgroundColor: 'rgba(255, 255, 0, 0.15)',
                    color: '#FFD700',
                    fontSize: '12px',
                    fontWeight: '700',
                    lineHeight: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    height: 'max-content',
                    width: 'max-content',
                  }}
                >
                  PINNED
                </div>
              )}
              {songData?.is_public_visible === false && (
                <PublicationBadge
                  text="Song Unpublished"
                  status="unpublished"
                  size="sm"
                  tooltip="Published Feels are hidden when the song is unpublished. Publish the song to show all published Feels."
                />
              )}
              {data?._refresh?.status &&
                data._refresh.status !== 'in_sync' &&
                data._refresh.status !== 'refreshable' && (
                <RefreshBadge
                  variant="badge"
                  status={data._refresh.status}
                  reason={data._refresh.reason}
                />
              )}
            </div>

            <div className={classes.buttonsWrapper}>
              {isLoadinAction ? (
                <div className={classes.buttonLoadingWrapper}>
                  <Loader isLoading={isLoadinAction} />
                </div>
              ) : (
                <>
                  <div 
                    style={{ position: 'relative', display: 'inline-block' }}
                    onMouseEnter={() => {
                      if (!data?.is_promotional) {
                        setShowPinTooltip(true)
                      }
                    }}
                    onMouseLeave={() => setShowPinTooltip(false)}
                  >
                    {data?.is_promotional && (
                      <ButtonOutline
                        disable={isFeelsReadOnly || !data?.is_promotional || isPinActionLoading}
                        color={isPinned ? 'red' : 'pink'}
                        size="small"
                        text={isPinActionLoading ? '...' : (isPinned ? 'UNPIN' : 'PIN')}
                        onClick={handlePinToggle}
                      />
                    )}
                    {showPinTooltip && !data?.is_promotional && (
                      <div 
                        style={{
                          position: 'absolute',
                          bottom: '40px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          background: 'rgba(0, 0, 0, 0.9)',
                          color: '#fff',
                          padding: '8px 12px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          whiteSpace: 'nowrap',
                          zIndex: 1000,
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          pointerEvents: 'none'
                        }}
                      >
                        Only Teases content can be pinned
                        <div 
                          style={{
                            position: 'absolute',
                            bottom: '-5px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 0,
                            height: 0,
                            borderLeft: '5px solid transparent',
                            borderRight: '5px solid transparent',
                            borderTop: '5px solid rgba(0, 0, 0, 0.9)'
                          }}
                        />
                      </div>
                    )}
                  </div>
                  {isInternal && (
                    <>
                      <ButtonOutline
                        color="pink"
                        size="small"
                        text={syncReported ? 'REPORTED' : reportSyncMutation.isPending ? 'REPORTING...' : 'REPORT SYNC'}
                        disable={reportSyncMutation.isPending || syncReported}
                        onClick={() => reportSyncMutation.mutate(id)}
                      />
                      <button
                        className={classes.shareIconButton}
                        onClick={() => setShowCollectionModal(true)}
                        type="button"
                        title="Add to Collection"
                        aria-label="Add to Collection"
                      >
                        <svg width="14" height="14" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="5">
                          <rect x="4" y="4" width="24" height="56" rx="3" />
                          <rect x="36" y="4" width="24" height="24" rx="3" />
                          <rect x="36" y="36" width="24" height="24" rx="3" />
                        </svg>
                      </button>
                      <button
                        className={classes.shareIconButton}
                        onClick={handleCopyShareURL}
                        type="button"
                        aria-label="Copy share link"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                        </svg>
                      </button>
                    </>
                  )}
                  <ButtonOutline
                    disable={isFeelsReadOnly}
                    color={'pink'}
                    size="small"
                    text={'EXPORT'}
                    onClick={() => {
                      navigate('/feel/' + data?.f_id + '/export')
                    }}
                  />

                  {data?._refresh?.status === 'refreshable' && (
                    <RefreshBadge
                      variant="icon"
                      size="md"
                      status="refreshable"
                      reason={data._refresh.reason}
                      isRefreshing={refreshSubmitted || refreshMutation.isPending}
                      disabled={isFeelsReadOnly || refreshSubmitted || refreshMutation.isPending}
                      onClick={() => refreshMutation.mutate()}
                    />
                  )}

                  {data && !data?.is_published && (
                    <ButtonOutline
                      disable={isFeelsReadOnly}
                      color={'green'}
                      size="small"
                      text={'PUBLISH'}
                      onClick={() => {
                        handleOnPublish()
                      }}
                    />
                  )}
                  {data?.is_published && (
                    <ButtonOutline
                      disable={isFeelsReadOnly}
                      color={'red'}
                      size="small"
                      text={'UNPUBLISH'}
                      onClick={() => {
                        handleOnUnpublish()
                      }}
                    />
                  )}
                  {isInternal && (
                    <ButtonOutline
                      disable={isFeelsReadOnly}
                      color={'red'}
                      size="small"
                      text={'DELETE'}
                      onClick={() => {
                        setIsPromptDelete(true)
                      }}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </StickyHeader>
      )}

      {!isLoading && data ? (
        <>
          <div className={classes.contentWrapper}>
            <div className={classes.videoContainer}>
              <video
                ref={videoElement}
                className={classes.videoPlayer}
                controls
                autoPlay={true}
                muted={true}
                onTimeUpdate={() => {
                  handleOnTimeUpdate()
                }}
                src={data?.preview?.video?.video?.url}
                controlsList="nofullscreen"
              />
            </div>
            <div className={classes.tagsColumnWrapper}>
              <div className={classes.tagsWrapper}>
                <div className={classes.lyrics}>
                  <ul className={classes.lyrics__text}>
                    {data.lyrics_sync.map(({ line }, index) => {
                      return <li key={line + index}>{line}</li>
                    })}
                  </ul>
                  {
                    <p
                      className={classes.lyrics__duration}
                    >{`${convertMillisecondsToSeconds(
                      data.video_duration
                    )} second${isPlural(
                      convertMillisecondsToSeconds(data.video_duration)
                    )}`}</p>
                  }
                </div>
                <TagInputSection
                  disabled={
                    isFeelsReadOnly ||
                    updateTagsMutation.isPending ||
                    isFetchingOfficialTags ||
                    isFetchingUserTags ||
                    isFetchingSuggestedTags
                  }
                  officialTags={officialTags?.tags}
                  userTags={userTags?.tags_user}
                  suggestedTags={suggestedTags?.tags || []}
                  handleTagsUpdated={newTags => {
                    handleUpdateTags(newTags)
                  }}
                  isFetchingOfficialTags={isFetchingOfficialTags}
                  isFetchingUserTags={isFetchingUserTags}
                  isFetchingSuggestedTags={
                    isFetchingSuggestedTags || isLoadingGenerateSuggestedTags
                  }
                  handleSuggestionTagClick={updateTagsMutation}
                  retchSuggestedTags={handleGenerateTags}
                  song={data}
                  isTagsUpdatingSuccess={updateTagsMutation.isSuccess}
                />
              </div>
              {/* )} */}
            </div>
          </div>
        </>
      ) : (
        <>
          {isError && (
            <h1 className={classes.errorText}>
              {'Oops, something went wrong'}
            </h1>
          )}
          {isLoading && <Loader isLoading={isLoading} />}
        </>
      )}
    </div>
  )
}
